import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Phone, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useAuth } from '@/contexts/AuthContext';
import { getDealerByPhone, sendOtp, verifyOtp, getDealerById } from '@/lib/api';

const LoginPage = () => {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [loading, setLoading] = useState(false);
  const [showNotRegisteredModal, setShowNotRegisteredModal] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const normalizePhone = (raw) => {
    const digits = raw.replace(/\D/g, '');
    return digits.length === 10 ? `+91${digits}` : `+${digits}`;
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();

    if (!phone || phone.length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    try {
      const formattedPhone = normalizePhone(phone);

      // Check dealer exists + is approved before burning an OTP SMS
      const checkRes = await getDealerByPhone(formattedPhone);
      const dealerCheck = checkRes.data;

      if (!dealerCheck) {
        setShowNotRegisteredModal(true);
        return;
      }

      if (dealerCheck.status === 'pending') {
        toast.error('Your account is pending approval. Please wait for admin approval.');
        return;
      }

      if (dealerCheck.status === 'blocked') {
        toast.error('Your account has been blocked. Please contact support.');
        return;
      }

      await sendOtp(formattedPhone);
      toast.success('OTP sent successfully!');
      setStep('otp');
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error(error.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const formattedPhone = normalizePhone(phone);
      const data = await verifyOtp(formattedPhone, otp, 'checkout');

      if (data.success) {
        if (data.isNewUser) {
          navigate('/register', { state: { phone: formattedPhone } });
        } else {
          const dealerRes = await getDealerById(data.userId);
          await login(dealerRes.data);
          toast.success('Welcome back!');
          navigate('/dashboard');
        }
      } else {
        toast.error(data.error || 'Invalid OTP');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error(error.message || 'Failed to verify OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="space-y-3 text-center pb-6">
          <div className="mx-auto w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold">EliteRentals</CardTitle>
          <CardDescription className="text-base">Channel Partner Portal - Sign in to continue</CardDescription>
          <p className="text-sm text-slate-700 mt-3 font-medium">
            Login here if you are already registered
          </p>
        </CardHeader>
        <CardContent>
          {step === 'phone' ? (
            <form onSubmit={handleSendOTP} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="phone"
                    data-testid="phone-input"
                    type="tel"
                    placeholder="Enter your 10-digit mobile number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="pl-10 h-12"
                    maxLength={10}
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                data-testid="send-otp-button"
                className="w-full h-12 text-base font-medium"
                disabled={loading || phone.length !== 10}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  'Send OTP'
                )}
              </Button>
              <div className="text-center pt-4 border-t mt-4">
                <p className="text-sm text-slate-600">
                  Not registered yet?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/request-access')}
                    className="text-blue-600 font-semibold hover:underline"
                    data-testid="request-access-link"
                  >
                    Request Access
                  </button>
                </p>
              </div>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <div className="space-y-4">
                <div className="text-sm text-center">
                  <p className="text-slate-600">OTP sent to</p>
                  <p className="font-semibold text-slate-900">+91 {phone}</p>
                  <button
                    type="button"
                    onClick={() => setStep('phone')}
                    className="text-sm text-slate-600 hover:text-slate-900 underline mt-1"
                  >
                    Change number
                  </button>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="otp" className="text-sm font-medium">Enter OTP</Label>
                  <div className="flex justify-center">
                    <InputOTP
                      data-testid="otp-input"
                      maxLength={6}
                      value={otp}
                      onChange={(value) => setOtp(value)}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>
              </div>
              <Button
                type="submit"
                data-testid="verify-otp-button"
                className="w-full h-12 text-base font-medium"
                disabled={loading || otp.length !== 6}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Continue'
                )}
              </Button>
              <button
                type="button"
                onClick={handleSendOTP}
                disabled={loading}
                className="w-full text-sm text-slate-600 hover:text-slate-900 underline"
              >
                Resend OTP
              </button>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Not Registered Modal */}
      {showNotRegisteredModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                <Building2 className="w-8 h-8 text-yellow-600" />
              </div>
              <CardTitle className="text-xl">Phone Number Not Registered</CardTitle>
              <CardDescription className="text-base mt-2">
                This phone number is not registered in our system.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600 text-center">
                To become a channel partner and earn commissions, you need to request access first.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setShowNotRegisteredModal(false);
                    navigate('/request-access');
                  }}
                  className="w-full h-12 text-base"
                  data-testid="modal-request-access-button"
                >
                  Request Access Now
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowNotRegisteredModal(false)}
                  className="w-full h-12"
                  data-testid="modal-cancel-button"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
